import { isString, without } from 'lodash-es'

import {
  EMPTY_LIST,
  EMPTY_STRING,
  HOST_NAME_LOCALHOST,
  REACT_INSTANCE_TYPEOF_SYMBOLS,
} from '../constants/common'
import { QA_ELEMENT_KEY, QA_NAME_DEFAULT } from '../constants/qa'

export function isDevelopmentEnvironment() {
  return process.env.NODE_ENV === 'development'
}

export function isBrowserEnvironment() {
  return typeof window !== 'undefined'
}

export function isNavigatorOffline() {
  return navigator.onLine === false
}

export function isBrowserOffline() {
  return isBrowserEnvironment() && isNavigatorOffline()
}

export function isHostNameLocalhost(hostname) {
  return hostname === HOST_NAME_LOCALHOST
}

export function getEnvBackendHost() {
  return process.env.BACKEND_HOST || null
}

export function isValueStringType(value) {
  return typeof value === 'string'
}

export function isValueNumberType(value) {
  return typeof value === 'number'
}

export function isValueObjectType(value) {
  return typeof value === 'object'
}

export function isValueFunctionType(value) {
  return typeof value === 'function'
}

export function isValueBooleanType(value) {
  return typeof value === 'boolean'
}

export function isValueSymbolType(value) {
  return typeof value === 'symbol'
}

export function isEmptyString(value) {
  return value === EMPTY_STRING
}

// Scoped to the Avatar/Icon use-case: validates that `value` can be rendered as
// a component type — a host-tag string, a function component, or a
// `$$typeof`-tagged wrapper object (forwardRef, memo, lazy, …). It deliberately
// rejects element/portal instances (which carry `$$typeof` too, yet can't be
// used as a component type) and does NOT recognise bare-symbol built-ins like
// React.Fragment or Suspense, since those never appear as an icon. The core job
// here is to reject a component that was round-tripped through JSON.stringify
// (e.g. a persisted draft): it loses its symbol and becomes a plain object,
// which must not reach React.createElement or it throws "Element type is
// invalid".
export function isValidElementType(value) {
  const typeSymbol = value?.$$typeof

  return (
    isValueStringType(value) ||
    isValueFunctionType(value) ||
    (isValueSymbolType(typeSymbol) &&
      !REACT_INSTANCE_TYPEOF_SYMBOLS.includes(typeSymbol))
  )
}

export function toggleItem(item, list) {
  if (list.includes(item)) {
    return without(list, item)
  } else {
    return list.concat(item)
  }
}

export function getQaSelectorButtonValue(qaName) {
  return `${qaName}Button`
}

export function getQaSelector(
  { context, def = QA_NAME_DEFAULT, value },
  key = QA_ELEMENT_KEY
) {
  if (!value) {
    return { [key]: def }
  }

  if (value && context) {
    return { [key]: `${value}_${context}` }
  }

  return { [key]: value }
}

export function getQaSelectorButton({ value, ...restConfig }, key) {
  return getQaSelector(
    { value: getQaSelectorButtonValue(value), ...restConfig },
    key
  )
}

export function isEllipsis(element) {
  if (!element?.current) {
    return false
  }

  return element.current.offsetWidth < element.current.scrollWidth
}

export function brToNewLine(text) {
  if (!isString(text)) {
    return EMPTY_STRING
  }

  return text.replace(/<br\s*\/?>\s*/g, '\n')
}

export function newLineToBr(text) {
  if (!isString(text)) {
    return EMPTY_STRING
  }

  return text.replace(/\n/g, '<br />')
}

export function isEqualNullables(value1 = null, value2 = null, isEqual) {
  return (
    (value1 === null && value2 === null) ||
    (value1 !== null && value2 !== null && isEqual(value1, value2))
  )
}

export function isEqualObjects(obj1, obj2) {
  if (obj1 !== obj2) {
    const keys1 = Object.keys(obj1)

    return (
      keys1.length === Object.keys(obj2).length &&
      keys1.every(key => Object.is(obj1[key], obj2[key]))
    )
  }

  return true
}

export function getObjectsSum(items, key = 'value') {
  return items.reduce(
    (accumulator, currentValue) => accumulator + currentValue[key],
    0
  )
}

export function getItemsIds(array, key = 'id') {
  return array.map(item => item[key])
}

export function filterVisibleItems(items, key = 'isVisible') {
  return items.filter(item => item[key])
}

export function insertValueInString({ insertedValue, position, str }) {
  return str.slice(0, position) + insertedValue + str.slice(position)
}

export function getMassActionIdParam({ ids, selectAllPages, selectedIds }) {
  const idsArray = Array.isArray(ids) ? ids : ids ? [ids] : null

  return selectAllPages
    ? undefined
    : idsArray?.length > 0
      ? idsArray
      : selectedIds
}

export function getMassActionExcludeIdParam({ excludeIds, selectAllPages }) {
  return selectAllPages && excludeIds.length > 0 ? excludeIds : undefined
}

// A select-all-pages selection cannot be resolved to items on the client, so it
// falls back to the checked rows of the loaded page.
export function getMassActionSelectedItems({
  excludeIds,
  pageItems = EMPTY_LIST,
  selectAllPages,
  selected,
}) {
  return selectAllPages
    ? pageItems.filter(item => !excludeIds.includes(item.id))
    : selected
}

export function getMultiValueActiveItems(values, getValue) {
  return values?.map(i => getValue(i))
}

export function isSamePage(pathname) {
  return pathname === window.location.pathname
}

export function isLastListItem(itemsCount, itemIndex) {
  return itemIndex === itemsCount - 1
}

export function calculateElementCenterY({
  container,
  elementTop,
  scrollElement,
  onChangeY,
}) {
  if (!scrollElement || !container) {
    return
  }

  const elementRect = scrollElement.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  const elementTopRect = elementTop || elementRect.top

  // Compute the scroll element’s vertical paddings and derive its inner height
  const elementComputedStyle = window.getComputedStyle(scrollElement)
  const elementPaddingTop = parseFloat(elementComputedStyle.paddingTop)
  const elementPaddingBottom = parseFloat(elementComputedStyle.paddingBottom)
  const elementHeight =
    elementRect.height - elementPaddingTop - elementPaddingBottom

  // The top of the element relative to the container
  const elementTopPosition =
    elementTopRect - containerRect.top + container.scrollTop
  const elementBottomPosition = elementTopPosition + elementHeight

  // Determine the boundaries of the visible area
  const visibleTop = Math.max(elementTopPosition, container.scrollTop)
  const visibleBottom = Math.min(
    elementBottomPosition,
    container.scrollTop + container.clientHeight
  )

  if (visibleBottom > visibleTop) {
    // Position the button exactly in the center of the visible part of the element
    const visibleCenterY = (visibleTop + visibleBottom) / 2

    // Attach the button position to the container
    const finalY = visibleCenterY - elementTopPosition

    onChangeY(finalY)
  }
}

export function isDefaultLogoSrc(src) {
  return src.includes('default_uk_logo') || src.includes('default_mobile_logo')
}

/**
 * The {@link identity} function returns its first argument unchanged. Useful as
 * a stable, referentially-constant default for optional transform callbacks.
 *
 * @template TValue
 * @param {TValue} value
 * @return {TValue}
 */
export function identity(value) {
  return value
}

export function getSectionCanCreatePermission(sections, sectionName) {
  return sections?.[sectionName]?.permissions?.can_create
}

export function getSectionCanEditPermission(sections, sectionName) {
  return sections?.[sectionName]?.permissions?.can_edit
}

export function getSectionCanViewPermission(sections, sectionName) {
  return sections?.[sectionName]?.permissions?.can_view
}

/**
 * bindHandlers
 * Wraps a map of event handlers so each handler is invoked with a shared context.
 *
 * What it does:
 * - Takes an object of handlers (e.g. { onClick, onMouseDown }).
 * - Returns a new object with the same keys, but each function is wrapped so it
 *   is called as: handler(event, ctx, ...args).
 * - This lets you “inject” a stable context (value, id, name, etc.) into every
 *   handler without creating ad-hoc closures at each call site.
 *
 * Params:
 * @param {Record<string, Function>} handlers  Map of handler props (keys like onClick).
 * @param {*}                        ctx       Context object injected as the 2nd argument.
 *
 * Returns:
 * @return {Record<string, Function>} A new handlers map with wrapped functions.
 *
 * Notes:
 * - This implementation assumes all values in `handlers` are functions.
 *   If non-function values are possible, filter them first.
 * - It does not compose with existing handlers on the element; if you need
 *   composition, merge separately (e.g., original then wrapped).
 * - The first argument remains the React/DOM event for compatibility.
 *
 * Example:
 *   const h = bindHandlers({ onClick: (e, ctx) => doSomething(ctx.id) }, { id: 42 });
 *   <Button {...h} /> // onClick receives (event, { id: 42 })
 */
export function bindHandlers(handlers = {}, ctx) {
  return Object.fromEntries(
    Object.entries(handlers).map(([k, fn]) => [
      k,
      (e, ...args) => fn(e, ctx, ...args),
    ])
  )
}

/**
 * An error-like object.
 * @typedef {Error | string} ErrorLike
 */

/**
 * The {@link error} function creates an `Error` instance from the passed
 * {@link ErrorLike} value.
 *
 * @example
 * ```
 *  const error1 = error("Something went wrong");
 *  // instance of `Error`
 *
 *  const error2 = error("Something went wrong", SyntaxError);
 *  // instance of `SyntaxError`
 *
 *  const error3 = error(new SyntaxError())
 *  // instance of `SyntaxError`
 * ```
 *
 * @param {ErrorLike} message - A string or `Error` instance.
 * @param {(message: string) => Error} Class - The class to create instance
 *     of error if the passed {@link message} is not an instance of `Error`.
 */
export function error(message, Class) {
  return message instanceof Error ? message : new Class(message)
}

/**
 * The {@link never} function throws an exception.
 *
 * ```
 *  function assertFoo(maybeFoo) {
 *    return maybeFoo === "foo" ? maybeFoo : never(`'${maybeFoo}' must be "foo"`);
 *  }
 * ```
 *
 * @param {ErrorLike} message - A string or an `Error` instance.
 * @param {(message: string) => Error} Class - The class to create instance of
 *   error if the passed {@link message} is not an instance of `Error`.
 *
 * @throws {@link Error} if {@link message} is a string or omitted
 * @throws {@link message} otherwise
 */
export function never(message = 'Never exception', Class = Error) {
  throw error(message, Class)
}

/**
 * @template {*} TValue
 * @param {TValue | null | undefined} value1
 * @param {TValue | null | undefined} value2
 * @param {(value1: TValue, value2: TValue) => boolean} isEqual
 *
 * @return {boolean}
 */
export function isEqualNullable(value1, value2, isEqual) {
  return value1 != null && value2 != null
    ? isEqual(value1, value2)
    : (value1 == null) === (value2 == null)
}
